import { apolloClient } from "@App/graphql/client";
import {
  GET_VEHICLE_RETURN_CONTRACT,
  type GetVehicleReturnContractResponse,
} from "@App/apis/graphql/fleet/vehicleReturn/queries";
import { type FleetErrorCode, mapErrorCode } from "@App/apis/graphql/fleet/errors";

type VehicleReturnContractResponse =
  | {
      ok: false;
      error: FleetErrorCode;
    }
  | {
      ok: true;
      contract: {
        agreementNumber: string;
        endDate: Date;
        authorizedReturnLocationIds: string[];
      };
    };

interface GetVehicleReturnContractPayload {
  organizationId: string;
  agreementNumber: string;
  numberPlate: string;
}

/**
 * Retrieves the vehicle return contract details for a given agreement and vehicle.
 */
async function getVehicleReturnContract({
  organizationId,
  agreementNumber,
  numberPlate,
}: GetVehicleReturnContractPayload): Promise<VehicleReturnContractResponse> {
  const { data, error } = await apolloClient.query<GetVehicleReturnContractResponse>({
    query: GET_VEHICLE_RETURN_CONTRACT,
    variables: {
      organizationId,
      agreementNumber,
      numberPlate,
    },
    errorPolicy: "all",
  });
  if (error) {
    const reason = error.graphQLErrors[0]?.extensions?.frontend?.reason_code;
    const fleetErrorCode = mapErrorCode(reason);
    return {
      ok: false,
      error: fleetErrorCode,
    };
  }
  const contract = data.organization?.Fleet?.ContractReturn;
  const hasMissingData =
    !contract ||
    !contract.baseContract ||
    !contract.baseContract.agreementNumber ||
    !contract.baseContract.endDate;
  if (hasMissingData) {
    return {
      ok: false,
      error: "contract_not_found",
    };
  }
  return {
    ok: true,
    contract: {
      agreementNumber: contract.baseContract.agreementNumber,
      endDate: new Date(contract.baseContract.endDate),
      authorizedReturnLocationIds: contract.authorizedReturnLocationIds ?? [],
    },
  };
}

export const vehicleReturn = {
  getVehicleReturnContract,
};
